/**
 * @unscoped — BoardPage 의 i18n — Cycle 57.
 *
 * cycle 51 calendar-i18n / cycle 52 workspace-i18n / cycle 54 mail-i18n /
 * cycle 55 onboarding-i18n 통합 패턴 일관. BoardSidebar 의 4 group × 다중 sub-item
 * 도입이 본 모듈의 진입점.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ----- BoardSidebar 메인 -----
  'board.nav_aria': { ko: '게시판 메뉴', en: 'Board menu', ja: '掲示板メニュー' },

  // ----- 4 group label -----
  'board.group.notice': { ko: '공지사항', en: 'Notices', ja: 'お知らせ' },
  'board.group.posts': { ko: '게시판', en: 'Board', ja: '掲示板' },
  'board.group.inquiry': { ko: '1:1 문의', en: '1:1 Inquiry', ja: '1:1お問い合わせ' },
  'board.group.faq': { ko: 'FAQ', en: 'FAQ', ja: 'FAQ' },

  // ----- 12 sub-item label -----
  'board.item.notice_all': { ko: '전체 공지', en: 'All notices', ja: 'すべてのお知らせ' },
  'board.item.notice_pinned': { ko: '필독 공지', en: 'Must-read', ja: '必読' },
  'board.item.posts_all': { ko: '전체 게시글', en: 'All posts', ja: 'すべての投稿' },
  'board.item.posts_free': { ko: '자유게시판', en: 'Free board', ja: '自由掲示板' },
  'board.item.posts_tech': { ko: '기술게시판', en: 'Tech board', ja: '技術掲示板' },
  'board.item.posts_info': { ko: '정보게시판', en: 'Info board', ja: '情報掲示板' },
  'board.item.inquiry_list': { ko: '내 문의 목록', en: 'My inquiries', ja: '私のお問い合わせ' },
  'board.item.inquiry_write': { ko: '문의 작성', en: 'New inquiry', ja: '新規お問い合わせ' },
  'board.item.faq_all': { ko: '전체 FAQ', en: 'All FAQ', ja: 'すべてのFAQ' },
  'board.item.faq_account': { ko: '계정/보안', en: 'Account / Security', ja: 'アカウント/セキュリティ' },
  'board.item.faq_chat': { ko: '채팅/알림', en: 'Chat / Notifications', ja: 'チャット/通知' },
  'board.item.faq_file': { ko: '파일/연동', en: 'Files / Integrations', ja: 'ファイル/連携' },

  // ===== Cycle 67 — BoardPage.tsx 본체 (4 tab + options + err 메시지) =====

  // ----- 공통 -----
  'board.btn.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'board.btn.create_post': { ko: '+ 작성', en: '+ Write', ja: '+ 作成' },
  'board.btn.create_inquiry': { ko: '+ 새 문의', en: '+ New inquiry', ja: '+ 新規お問い合わせ' },
  'board.btn.register': { ko: '등록', en: 'Register', ja: '登録' },
  'board.placeholder.title': { ko: '제목', en: 'Title', ja: 'タイトル' },
  'board.placeholder.content': { ko: '내용', en: 'Content', ja: '内容' },
  'board.suffix.view_count': { ko: '· 조회 {n}', en: '· views {n}', ja: '· 閲覧 {n}' },

  // ----- NoticeTab -----
  'board.notice.heading_all': { ko: '공지사항', en: 'Notices', ja: 'お知らせ' },
  'board.notice.heading_pinned': { ko: '필독 공지', en: 'Must-read notices', ja: '必読のお知らせ' },
  'board.notice.label_important_only': { ko: '중요 공지만', en: 'Important only', ja: '重要のみ' },
  'board.notice.empty': { ko: '공지가 없습니다.', en: 'No notices.', ja: 'お知らせはありません。' },
  'board.notice.err_load': {
    ko: '공지 로드 실패: {msg}',
    en: 'Failed to load notices: {msg}',
    ja: 'お知らせ読み込み失敗: {msg}',
  },
  'board.notice.err_detail': {
    ko: '상세 로드 실패: {msg}',
    en: 'Failed to load detail: {msg}',
    ja: '詳細読み込み失敗: {msg}',
  },

  // ----- PostTab -----
  'board.post.heading_all': { ko: '전체 게시글', en: 'All posts', ja: 'すべての投稿' },
  'board.post.heading_free': { ko: '자유게시판', en: 'Free board', ja: '自由掲示板' },
  'board.post.heading_tech': { ko: '기술게시판', en: 'Tech board', ja: '技術掲示板' },
  'board.post.heading_info': { ko: '정보게시판', en: 'Info board', ja: '情報掲示板' },
  'board.post.label_category': { ko: '카테고리', en: 'Category', ja: 'カテゴリ' },
  'board.post.aria_draft_category': { ko: '작성 카테고리', en: 'Post category', ja: '投稿カテゴリ' },
  'board.post.empty': { ko: '게시글이 없습니다.', en: 'No posts.', ja: '投稿がありません。' },
  'board.post.err_required': {
    ko: '제목과 내용을 입력하세요',
    en: 'Please enter title and content',
    ja: 'タイトルと内容を入力してください',
  },
  'board.post.err_list': {
    ko: '목록 로드 실패: {msg}',
    en: 'Failed to load list: {msg}',
    ja: '一覧読み込み失敗: {msg}',
  },
  'board.post.err_detail': {
    ko: '상세 로드 실패: {msg}',
    en: 'Failed to load detail: {msg}',
    ja: '詳細読み込み失敗: {msg}',
  },
  'board.post.err_create': {
    ko: '작성 실패: {msg}',
    en: 'Create failed: {msg}',
    ja: '作成失敗: {msg}',
  },

  // ----- POST_FILTER_OPTIONS / POST_DRAFT_OPTIONS -----
  'board.opt.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'board.opt.free': { ko: '자유', en: 'Free', ja: '自由' },
  'board.opt.tech': { ko: '기술', en: 'Tech', ja: '技術' },
  'board.opt.info': { ko: '정보', en: 'Info', ja: '情報' },

  // ----- INQUIRY_CATEGORY_OPTIONS (7) -----
  'board.inquiry.cat.account': { ko: '계정', en: 'Account', ja: 'アカウント' },
  'board.inquiry.cat.notification': { ko: '알림', en: 'Notification', ja: '通知' },
  'board.inquiry.cat.file': { ko: '파일', en: 'File', ja: 'ファイル' },
  'board.inquiry.cat.security': { ko: '보안', en: 'Security', ja: 'セキュリティ' },
  'board.inquiry.cat.chat': { ko: '채팅', en: 'Chat', ja: 'チャット' },
  'board.inquiry.cat.integration': { ko: '연동', en: 'Integration', ja: '連携' },
  'board.inquiry.cat.other': { ko: '기타', en: 'Other', ja: 'その他' },

  // ----- FaqTab -----
  'board.faq.heading_all': { ko: '전체 FAQ', en: 'All FAQ', ja: 'すべてのFAQ' },
  'board.faq.heading_category': { ko: 'FAQ — {category}', en: 'FAQ — {category}', ja: 'FAQ — {category}' },
  'board.faq.search_placeholder': { ko: '질문 검색…', en: 'Search questions…', ja: '質問検索...' },
  'board.faq.search_aria': { ko: 'FAQ 검색', en: 'Search FAQ', ja: 'FAQ検索' },
  'board.faq.empty': { ko: '검색 결과가 없습니다.', en: 'No search results.', ja: '検索結果がありません。' },
  'board.faq.err_load': {
    ko: 'FAQ 로드 실패: {msg}',
    en: 'Failed to load FAQ: {msg}',
    ja: 'FAQ読み込み失敗: {msg}',
  },
  'board.faq.err_feedback': {
    ko: '피드백 실패: {msg}',
    en: 'Feedback failed: {msg}',
    ja: 'フィードバック失敗: {msg}',
  },

  // ----- InquiryTab -----
  'board.inquiry.heading_list': { ko: '내 문의 목록', en: 'My inquiries', ja: '私のお問い合わせ' },
  'board.inquiry.heading_create': { ko: '문의 작성', en: 'New inquiry', ja: '新規お問い合わせ' },
  'board.inquiry.aria_category': { ko: '문의 카테고리', en: 'Inquiry category', ja: 'お問い合わせカテゴリ' },
  'board.inquiry.empty': { ko: '문의가 없습니다.', en: 'No inquiries.', ja: 'お問い合わせはありません。' },
  'board.inquiry.status_pending': { ko: '대기', en: 'Pending', ja: '待機' },
  'board.inquiry.status_answered': { ko: '답변완료', en: 'Answered', ja: '回答済み' },
  'board.inquiry.err_required': {
    ko: '카테고리·제목·내용 모두 입력하세요',
    en: 'Please enter category, title, and content',
    ja: 'カテゴリ・タイトル・内容をすべて入力してください',
  },
  'board.inquiry.err_list': {
    ko: '문의 목록 로드 실패: {msg}',
    en: 'Failed to load inquiries: {msg}',
    ja: 'お問い合わせ一覧読み込み失敗: {msg}',
  },
  'board.inquiry.err_create': {
    ko: '작성 실패: {msg}',
    en: 'Create failed: {msg}',
    ja: '作成失敗: {msg}',
  },
};

export type BoardI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 57 — BoardPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) 옵션 — calendar-i18n / workspace-i18n / mail-i18n 와 동일.
 */
export function tBoard(key: BoardI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getBoardLang(): Lang { return getSharedLang(); }
export function setBoardLang(value: Lang): void { setSharedLang(value); }
export function subscribeBoardLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
